import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import { Heart, MapPin, Star, Play, Pause, Volume2, VolumeX, ChevronUp, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Global sound preference, persists across videos like TikTok
let globalSoundOn = false;

// Windowed mount, we only keep <video> elements rendered for slots within
// this many steps of the active card. Everything outside stays as a cheap
// placeholder div so the snap-scroll geometry is preserved while freeing up
// the browser's media pipeline + memory (big mobile win).
const MOUNT_BEFORE = 1;
const MOUNT_AFTER = 2;

function VideoCard({ video, isActive, isNext, isAfterNext, isMounted, likedIds, onLike, isLoggedIn, soundOn, onToggleSound }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [localMuted, setLocalMuted] = useState(!soundOn);
  const navigate = useNavigate();

  // Ref always holds latest soundOn to avoid stale closures
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  // Handle play/pause when this video becomes active
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isActive) {
      el.currentTime = 0;
      // ALWAYS start muted, autoplay policy guarantees this succeeds
      el.muted = true;
      el.play().then(() => {
        setPlaying(true);
        // Now that play() succeeded, apply the user's sound preference
        // Unmuting an already-playing video does NOT require a new user gesture
        const wantSound = soundOnRef.current;
        el.muted = !wantSound;
        el.volume = wantSound ? 1 : 0;
        setLocalMuted(!wantSound);
      }).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Sync sound when global preference changes while this video is active
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !isActive || el.paused) return;
    el.muted = !soundOn;
    el.volume = soundOn ? 1 : 0;
    setLocalMuted(!soundOn);
  }, [soundOn, isActive]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true));
    } else {
      el.pause();
      setPlaying(false);
    }
    setShowPlayIcon(true);
    setTimeout(() => setShowPlayIcon(false), 600);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const el = videoRef.current;
    if (!el) return;

    const newSoundOn = !soundOn;

    // Direct DOM manipulation inside user click handler
    el.muted = !newSoundOn;
    el.volume = newSoundOn ? 1 : 0;
    setLocalMuted(!newSoundOn);

    // If unmuting and video is paused, play it (browser requires play() from user gesture)
    if (newSoundOn && el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => {});
    }

    // Update global preference
    onToggleSound(newSoundOn);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      toast.info('Sign in to save videos');
      navigate('/login');
      return;
    }
    onLike(video.video_id);
  };

  const isLiked = likedIds.has(video.video_id);

  // Outside the mount window, render only a lightweight placeholder so the
  // snap-scroll container still has the right height but no <video> element
  // is created. The poster image keeps the slot visually recognisable while
  // the user is scrolling past.
  if (!isMounted) {
    return (
      <div
        className="relative w-full h-full bg-black snap-start snap-always"
        data-testid={`video-card-placeholder-${video.video_id}`}
      >
        {video.poster_url && (
          <img
            src={video.poster_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-contain opacity-60"
            aria-hidden="true"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-full bg-black snap-start snap-always flex items-center justify-center"
      onClick={togglePlay}
      data-testid={`video-card-${video.video_id}`}
    >
      {video.video_url ? (
        <video
          ref={videoRef}
          src={video.video_url}
          poster={video.poster_url || undefined}
          className="w-full h-full object-contain feed-video"
          loop
          playsInline
          muted
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          // Preload aggressively for the active slot AND the two slots after it
          // so a swipe shows an already-buffered frame instantly. Neighbours
          // outside that window just fetch metadata (~ few KB).
          preload={isActive || isNext || isAfterNext ? 'auto' : 'metadata'}
          data-testid={`video-player-${video.video_id}`}
        />
      ) : (
        // Tutorial / demo posts have no video file, render the poster image
        // directly so iOS doesn't inject native PiP / fullscreen controls
        // over an empty <video> element.
        <img
          src={video.poster_url}
          alt={video.caption || video.brand_name}
          className="w-full h-full object-contain"
          data-testid={`video-poster-${video.video_id}`}
        />
      )}

      {/* Play/Pause indicator */}
      {showPlayIcon && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center animate-ping-once">
            {playing ? <Pause className="h-8 w-8 text-white" /> : <Play className="h-8 w-8 text-white ml-1" />}
          </div>
        </div>
      )}

      {/* Right-side actions, z-30 to be above bottom overlay */}
      <div className="absolute right-4 bottom-32 flex flex-col items-center gap-5 z-30">
        {/* Like */}
        <button onClick={handleLike} className="flex flex-col items-center gap-1 pointer-events-auto" data-testid={`like-btn-${video.video_id}`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isLiked ? 'bg-red-500' : 'bg-white/15 backdrop-blur-sm'}`}>
            <Heart className={`h-5 w-5 ${isLiked ? 'text-white fill-current' : 'text-white'}`} />
          </div>
          <span className="text-white text-xs font-medium">{video.likes || 0}</span>
        </button>

        {/* Mute toggle */}
        <button
          onClick={toggleMute}
          className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center pointer-events-auto"
          data-testid={`mute-btn-${video.video_id}`}
        >
          {localMuted ? <VolumeX className="h-5 w-5 text-white" /> : <Volume2 className="h-5 w-5 text-white" />}
        </button>

        {/* Provider avatar */}
        <Link to={`/@${video.username}`} onClick={e => e.stopPropagation()} className="w-11 h-11 rounded-full border-2 border-[#D4AF37] overflow-hidden bg-[#D4AF37] flex items-center justify-center" data-testid={`provider-avatar-${video.video_id}`}>
          {video.profile_image
            ? <img src={video.profile_image} alt="" className="w-full h-full object-cover" />
            : <span className="text-white text-sm font-bold">{video.brand_name?.charAt(0)}</span>}
        </Link>
      </div>

      {/* Bottom overlay, provider info + CTA */}
      <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none">
        <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-24 pb-6 px-5 pointer-events-auto">
          <div className="mb-3">
            <Link to={`/@${video.username}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-2 hover:underline">
              <span className="text-white font-bold text-base">{video.brand_name}</span>
            </Link>
            <div className="flex items-center gap-3 mt-1">
              {video.location && (
                <span className="flex items-center gap-1 text-white/70 text-xs">
                  <MapPin className="h-3 w-3" />{video.location}
                </span>
              )}
              {video.rating > 0 && (
                <span className="flex items-center gap-1 text-[#D4AF37] text-xs">
                  <Star className="h-3 w-3 fill-current" />{video.rating.toFixed(1)}
                </span>
              )}
            </div>
          </div>

          {video.caption && (
            <p className="text-white/90 text-sm mb-3 line-clamp-2">{video.caption}</p>
          )}

          {video.service_id && (
            <Link
              to={`/service/${video.service_id}`}
              onClick={e => e.stopPropagation()}
              className="flex items-center justify-between bg-[#D4AF37] rounded-xl px-5 py-3 transition-transform active:scale-[0.98]"
              data-testid={`book-now-${video.video_id}`}
            >
              <div>
                <p className="text-[#1A1A1A] font-bold text-sm">{video.service_name}</p>
                {video.service_price && (
                  <p className="text-[#1A1A1A]/70 text-xs">from £{video.service_price}</p>
                )}
              </div>
              <span className="bg-[#1A1A1A] text-white text-sm font-bold px-4 py-1.5 rounded-lg">
                Book Now
              </span>
            </Link>
          )}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute top-4 left-0 right-0 flex justify-center z-10 pointer-events-none">
        <ChevronUp className="h-5 w-5 text-white/40 animate-bounce" />
      </div>
    </div>
  );
}

export default function VideoFeed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const includeTutorials = searchParams.get('include_tutorials') === 'true';
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [likedIds, setLikedIds] = useState(new Set());
  const [soundOn, setSoundOn] = useState(globalSoundOn);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const loadingMore = useRef(false);

  const handleToggleSound = useCallback((newState) => {
    globalSoundOn = newState;
    setSoundOn(newState);
  }, []);

  const fetchFeed = useCallback(async (skip = 0, retries = 5) => {
    try {
      const tutFlag = includeTutorials ? '&include_tutorials=true' : '';
      const res = await api.get(`/feed?skip=${skip}&limit=10${tutFlag}`);
      const newVideos = res.data || [];
      if (skip === 0) {
        setVideos(newVideos);
      } else {
        setVideos(prev => [...prev, ...newVideos]);
      }
      setHasMore(newVideos.length === 10);
      setLoading(false);
      loadingMore.current = false;
    } catch (err) {
      const status = err.response?.status;
      const isTransient = status === 503 || status === 502 || !err.response;
      if (retries > 0 && isTransient) {
        await new Promise(r => setTimeout(r, 3000));
        return fetchFeed(skip, retries - 1);
      }
      console.error('Feed fetch error:', err);
      setLoading(false);
      loadingMore.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeTutorials]);

  const fetchLiked = useCallback(async () => {
    try {
      const res = await api.get('/feed/liked');
      setLikedIds(new Set(res.data || []));
    } catch (err) { console.error('Liked fetch error:', err); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFeed();
    if (user) fetchLiked();
  }, [fetchFeed, fetchLiked, user]);

  const handleLike = async (videoId) => {
    const wasLiked = likedIds.has(videoId);
    setLikedIds(prev => {
      const next = new Set(prev);
      if (wasLiked) next.delete(videoId); else next.add(videoId);
      return next;
    });
    setVideos(prev => prev.map(v =>
      v.video_id === videoId ? { ...v, likes: (v.likes || 0) + (wasLiked ? -1 : 1) } : v
    ));
    try {
      await api.post(`/feed/${videoId}/like`);
    } catch (err) {
      console.error('Like toggle failed:', err);
      setLikedIds(prev => {
        const next = new Set(prev);
        if (wasLiked) next.add(videoId); else next.delete(videoId);
        return next;
      });
      setVideos(prev => prev.map(v =>
        v.video_id === videoId ? { ...v, likes: (v.likes || 0) + (wasLiked ? 1 : -1) } : v
      ));
    }
  };

  const lastVideoRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore.current) {
        loadingMore.current = true;
        fetchFeed(videos.length);
      }
    }, { threshold: 0.5 });
    if (node) observerRef.current.observe(node);
  }, [hasMore, videos.length, fetchFeed]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = parseInt(entry.target.dataset.index, 10);
          if (!isNaN(idx)) setActiveIndex(idx);
        }
      });
    }, { root: container, threshold: 0.6 });

    const items = container.querySelectorAll('[data-video-item]');
    items.forEach(item => observer.observe(item));
    return () => observer.disconnect();
  }, [videos]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50 text-center px-6">
        <Play className="h-16 w-16 text-white/20 mb-4" />
        <h2 className="text-white text-xl font-bold mb-2">No videos yet</h2>
        <p className="text-white/50 text-sm mb-6">Providers haven't uploaded any videos yet. Check back soon!</p>
        <button onClick={() => navigate('/discover')} className="bg-[#D4AF37] text-[#1A1A1A] font-bold px-6 py-3 rounded-xl">
          Browse Services
        </button>
        <button onClick={() => navigate('/discover')} className="text-white/40 text-sm mt-4 hover:text-white">Go back</button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50" data-testid="video-feed">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 pt-4 pb-2 bg-gradient-to-b from-black/60 to-transparent pointer-events-auto">
        <button onClick={() => navigate('/discover')} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center" data-testid="feed-back-btn">
          <ArrowLeft className="h-5 w-5 text-white" />
        </button>
        <h1 className="text-white font-bold text-lg tracking-wide">ZURIYA</h1>
        <Link to="/discover" className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/20 transition-colors" data-testid="feed-discover-link">
          Discover
        </Link>
      </div>

      {includeTutorials && (
        <div
          className="absolute top-16 left-3 right-3 z-30 rounded-lg bg-amber-500/95 text-amber-950 px-3 py-2 text-xs font-medium flex items-center gap-2 backdrop-blur-sm"
          data-testid="feed-tutorial-mode-banner"
        >
          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-700 text-amber-50 text-[10px] font-bold">i</span>
          Tutorial mode active. Demo posts visible for filming.
        </div>
      )}

      {/* Scroll container */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-y-scroll snap-y snap-mandatory scrollbar-none"
        style={{ scrollSnapType: 'y mandatory' }}
        data-testid="video-scroll-container"
      >
        {videos.map((video, index) => {
          const isMounted = index >= activeIndex - MOUNT_BEFORE && index <= activeIndex + MOUNT_AFTER;
          return (
            <div
              key={video.video_id}
              data-video-item
              data-index={index}
              ref={index === videos.length - 1 ? lastVideoRef : null}
              className="w-full h-screen"
              style={{ scrollSnapAlign: 'start' }}
            >
              <VideoCard
                video={video}
                isActive={index === activeIndex}
                isNext={index === activeIndex + 1}
                isAfterNext={index === activeIndex + 2}
                isMounted={isMounted}
                likedIds={likedIds}
                onLike={handleLike}
                isLoggedIn={!!user}
                soundOn={soundOn}
                onToggleSound={handleToggleSound}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
